
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Edit, Trash2, Eye, EyeOff, Navigation } from "lucide-react";
import { ClientStatus } from "@/types/clientStatus";
import { useDeleteClientStatus, useUpdateClientStatus } from "@/hooks/useClientStatuses";

interface ClientStatusGridProps {
  statuses: ClientStatus[];
  onEdit: (status: ClientStatus) => void;
}

export const ClientStatusGrid = ({ statuses, onEdit }: ClientStatusGridProps) => {
  const deleteMutation = useDeleteClientStatus();
  const updateMutation = useUpdateClientStatus();

  const handleDelete = async (status: ClientStatus) => {
    if (confirm(`هل أنت متأكد من حذف الحالة "${status.name_ar}"؟`)) {
      try {
        await deleteMutation.mutateAsync(status.id);
      } catch (error) {
        console.error("Error deleting status:", error);
      }
    }
  };

  const handleToggleActive = async (status: ClientStatus) => {
    try {
      await updateMutation.mutateAsync({
        id: status.id,
        is_active: !status.is_active
      });
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  const handleToggleTopNav = async (status: ClientStatus) => {
    try {
      console.log(`Toggling top nav visibility for status: ${status.name_ar}, current: ${status.show_in_top_nav}`);
      await updateMutation.mutateAsync({
        id: status.id,
        show_in_top_nav: !status.show_in_top_nav
      });
      console.log(`Successfully toggled top nav visibility for status: ${status.name_ar}`);
    } catch (error) {
      console.error("Error toggling top nav visibility:", error);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {statuses.map((status) => (
        <Card 
          key={status.id} 
          className={`relative transition-all duration-200 hover:shadow-md ${
            !status.is_active ? 'opacity-60' : ''
          }`}
        >
          <CardContent className="p-4">
            <div className="space-y-3">
              {/* Header with status name and toggle */}
              <div className="flex items-start justify-between">
                <div className="space-y-1 flex-1">
                  <h3 className="font-semibold text-gray-900">{status.name_ar}</h3>
                  <p className="text-sm text-gray-600">{status.name_en}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleTopNav(status)}
                    className={`text-xs border transition-all duration-200 ${
                      status.show_in_top_nav 
                        ? 'text-blue-600 hover:text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100' 
                        : 'text-gray-400 hover:text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    title={status.show_in_top_nav ? 'مخفي في القائمة العلوية (اضغط لإظهار)' : 'ظاهر في القائمة العلوية (اضغط لإخفاء)'}
                  >
                    <Navigation className="w-4 h-4" />
                    <span className="mr-1 text-xs">
                      {status.show_in_top_nav ? 'مظهور' : 'مخفي'}
                    </span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(status)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {status.is_active ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Status preview */}
              <div className="space-y-2">
                <Badge 
                  className="text-white text-xs"
                  style={{ backgroundColor: status.color }}
                >
                  {status.name_ar}
                </Badge>
                <div className="text-xs text-gray-500">
                  <p>المفتاح: {status.status_key}</p>
                  <p>الترتيب: {status.sort_order}</p>
                </div>
              </div>

              {/* Status indicators */}
              <div className="flex gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${status.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className="text-gray-600">{status.is_active ? 'نشط' : 'غير نشط'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Navigation className={`w-3 h-3 ${status.show_in_top_nav ? 'text-blue-500' : 'text-gray-400'}`} />
                  <span className="text-gray-600">{status.show_in_top_nav ? 'ظاهر بالقائمة' : 'مخفي من القائمة'}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(status)}
                  className="flex-1 text-xs"
                >
                  <Edit className="w-3 h-3 ml-1" />
                  تعديل
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(status)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
